import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import WidgetHeader from 'src/components/widgets/widget-header';
import { IAds } from 'src/modules/ads';
import { DATALAYER_TYPE, sendDataLayerGlobal } from 'src/utils/datalayer/send-datalayer';
import { KEY_ADS_POSITION } from '../ads/dynamicAds';

const CardGallery = dynamic(import('src/components/cards/card-gallery'));
const DynamicAds = dynamic(import('src/components/ads/dynamicAds'), { ssr: true });

type Props = {
  unsetBottom: boolean;
  ads?: IAds[];
  highlightText?: any;
  subMenu: any;
  navBar: any;
};

interface Tag {
  id: number;
  name: string;
}

const SectionHighlight = ({ unsetBottom = true, ads = [], highlightText = [], navBar }: Props) => {
  const router: any = useRouter();
  const subMenu = navBar[0]?.sub;
  const title = navBar[0]?.nameTh;
  const link = navBar[0]?.link;

  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [slideDirection, setSlideDirection] = useState<'up' | 'down' | null>(null);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const isAnimatingRef = useRef<boolean>(false);
  const selectedIndexRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ใช้ ref คู่กับ state เพื่อให้ interval อ่านค่าล่าสุดได้
  const updateIndex = useCallback((index: number) => {
    setSelectedIndex(index);
    selectedIndexRef.current = index;
  }, []);

  const updateAnimating = useCallback((value: boolean) => {
    setIsAnimating(value);
    isAnimatingRef.current = value;
  }, []);

  // ฟังก์ชันเลื่อนไปข้างหน้า (ใช้ทั้ง auto และ manual)
  const goToNext = useCallback(() => {
    if (isAnimatingRef.current || !subMenu?.length) return;

    const currentIdx = selectedIndexRef.current;
    const nextIndex = (currentIdx + 1) % subMenu.length;

    setSlideDirection('up');
    updateAnimating(true);

    setTimeout(() => {
      updateIndex(nextIndex);
    }, 150);

    setTimeout(() => {
      updateAnimating(false);
      setSlideDirection(null);
    }, 500);
  }, [subMenu?.length, updateIndex, updateAnimating]);

  // เริ่ม auto-slide
  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(goToNext, 5000);
  }, [goToNext]);

  // หยุด auto-slide
  const stopAutoSlide = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // เริ่ม auto-slide เมื่อ component mount (SSR-safe: useEffect ทำงานแค่ client)
  useEffect(() => {
    if (!subMenu?.length || subMenu.length <= 1) return;

    startAutoSlide();
    return () => {
      stopAutoSlide();
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    };
  }, [subMenu?.length, startAutoSlide, stopAutoSlide]);

  // กดเอง: หยุด auto ชั่วคราว 10 วิ แล้วเริ่มใหม่
  const handleTagClick = (index: number, name: string): void => {
    if (selectedIndex === index || isAnimating) return;

    const direction = index > selectedIndex ? 'up' : 'down';

    setSlideDirection(direction);
    updateAnimating(true);

    setTimeout(() => {
      updateIndex(index);
    }, 150);

    setTimeout(() => {
      updateAnimating(false);
      setSlideDirection(null);
    }, 500);

    // หยุด auto-slide แล้วเริ่มใหม่หลัง 10 วิ
    stopAutoSlide();
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
    pauseTimeoutRef.current = setTimeout(() => {
      startAutoSlide();
    }, 7000);

    sendDataLayerGlobal({
      type: DATALAYER_TYPE.TRACK_POSITION,
      router: router?.pathname,
      section: 'section-1',
      position: `ใต้ : section-highlight`,
      data: {
        title: name,
        heading: `เมนู-section-1`,
        index: index + 1
      }
    });
  };

  // หา data ของ subMenu ที่เลือก
  const currentSubMenu = subMenu?.[selectedIndex];
  const galleryData = currentSubMenu?.data || [];

  // Desktop: แบ่งรูปเป็น 4 columns (column ละ 2 รูป)
  const desktopColumns = [
    { images: galleryData.slice(0, 2), pattern: 'tall-short' },
    { images: galleryData.slice(2, 4), pattern: 'short-tall' },
    { images: galleryData.slice(4, 6), pattern: 'tall-short' },
    { images: galleryData.slice(6, 8), pattern: 'short-tall' }
  ];

  // Mobile: แบ่งรูปเป็น 3 columns (column ละ 3 รูป)
  const mobileColumns = [
    { images: galleryData.slice(0, 3), pattern: 'col-1' },
    { images: galleryData.slice(3, 6), pattern: 'col-2' },
    { images: galleryData.slice(6, 9), pattern: 'col-3' }
  ];

  // สร้าง animation class
  const getAnimationClass = (): string => {
    if (!isAnimating || !slideDirection) return '';
    return `slide-${slideDirection}`;
  };

  return (
    <>
      <SectionWrapper id='section-1' className={`${!unsetBottom ? 'padding-bottom' : ''} highlight`}>
        {!isEmpty(currentSubMenu) && (
          <>
            <WidgetHeader as='h2' titleColor='#000000' title={title} variant='line' position='center' showViewMore={false} viewMoreLink={link} />
            <div className='container'>
              {/* Desktop Gallery */}
              <div className='isDesktop'>
                <div className='gallery-wrapper'>
                  <div className={`gallery-grid ${getAnimationClass()}`}>
                    {desktopColumns.map((col, colIndex) => (
                      <div key={colIndex} className={`column ${col.pattern}`}>
                        {col.images.map((item: any, imgIndex: number) => {
                          const itemNumber = colIndex * 2 + imgIndex + 1;
                          const isVertical = [1, 4, 5, 8].includes(itemNumber);
                          return (
                            <div key={item?.id || imgIndex} className={`gallery-item ${imgIndex === 0 ? 'first' : 'second'}`}>
                              {item && (
                                <>
                                  <CardGallery
                                    data={item}
                                    showTitle={false}
                                    showBlurb={false}
                                    showByline={false}
                                    showTime={false}
                                    vertical_image={isVertical}
                                    type={isVertical ? 'CARD_LARGE' : 'CARD_SMALL'}
                                    eventDataLayer={{
                                      type: DATALAYER_TYPE.TRACK_POSITION,
                                      router: router?.pathname,
                                      section: 'section-1',
                                      position: `ใต้ : section-highlight`,
                                      data: {
                                        title: item?.title,
                                        heading: `section-1`,
                                        index: colIndex * 2 + imgIndex + 1
                                      }
                                    }}
                                  />
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mobile Gallery */}
              <div className='isMobile'>
                <div className='gallery-wrapper'>
                  <div className={`gallery-grid-mobile ${getAnimationClass()}`}>
                    {mobileColumns.map((col, colIndex) => (
                      <div key={colIndex} className={`column-mobile ${col.pattern}`}>
                        {/* {imgIndex + 1} */}
                        {col.images.map((item: any, imgIndex: number) => (
                          <div key={item?.id || imgIndex} className={`gallery-item item-${imgIndex + 1}`}>
                            {item && (
                              <CardGallery
                                data={item}
                                showTitle={false}
                                type='CARD_SMALL'
                                eventDataLayer={{
                                  type: DATALAYER_TYPE.TRACK_POSITION,
                                  router: router?.pathname,
                                  section: 'section-1',
                                  position: `ใต้ : section-highlight`,
                                  data: {
                                    title: item?.title,
                                    heading: `section-1`,
                                    index: colIndex * 2 + imgIndex + 1
                                  }
                                }}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sub Menu Buttons */}
              <div className='body-list-sub-menu'>
                {subMenu.map((item: any, index: number) => (
                  <button key={item.id} className={`sub-menu-button ${selectedIndex === index ? 'active' : ''}`} onClick={() => handleTagClick(index, item.nameTh)}>
                    {item.nameTh}
                  </button>
                ))}
              </div>

              {!isEmpty(ads) && <DynamicAds position={KEY_ADS_POSITION.BILLBOARD} data={ads} />}
              {/* {unsetBottom && <div className='border-bottom' />} */}
            </div>
          </>
        )}
      </SectionWrapper>
    </>
  );
};

const SectionWrapper = styled.section`
  margin-top: -15px;
  padding: 30px 0;
  padding-bottom: 0;
  &.padding-bottom {
    padding-bottom: 30px;
  }

  .container {
    padding: 0;
    @media (max-width: 1024px) {
      padding: 0;
    }
  }

  .border-bottom {
    margin-top: 30px;
    border-bottom: 1px solid #5f5f5f42;
  }

  /* ========== Gallery Wrapper (สำหรับ overflow) ========== */
  .gallery-wrapper {
    overflow: hidden;
    position: relative;
  }

  /* ========== Desktop Gallery ========== */
  .gallery-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    height: 750px;
    margin: 2.5rem 0;

    /* Performance optimization */
    will-change: transform, opacity;
    transform: translateY(0);
    opacity: 1;
    transition:
      transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    /* Slide Up Animation (กดไปทางขวา) */
    &.slide-up {
      animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Slide Down Animation (กดไปทางซ้าย) */
    &.slide-down {
      animation: slideDown 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  /* ========== Mobile Gallery ========== */
  .gallery-grid-mobile {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    height: 450px;

    /* Performance optimization */
    will-change: transform, opacity;
    transform: translateY(0);
    opacity: 1;
    transition:
      transform 0.5s cubic-bezier(0.4, 0, 0.2, 1),
      opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    &.slide-up {
      animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &.slide-down {
      animation: slideDown 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    }
  }

  /* Keyframes */
  @keyframes slideUp {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    50% {
      transform: translateY(-30px);
      opacity: 0;
    }
    51% {
      transform: translateY(30px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    50% {
      transform: translateY(30px);
      opacity: 0;
    }
    51% {
      transform: translateY(-30px);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* Sub Menu Buttons */
  .body-list-sub-menu {
    display: flex;
    gap: 10px;
    margin-top: 1rem;
    overflow-x: auto;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    justify-content: space-between;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    padding-bottom: 5px;
  }

  .sub-menu-button {
    flex-shrink: 0;
    min-width: fit-content;
    padding: 8px 20px;
    border: 0;
    border-radius: 8px;
    background-color: #d9d9d8;
    color: #0b0b0a;
    font-size: 20px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    @media (max-width: 768px) {
      font-size: 16px;
      padding: 8px 16px;
    }
  }

  .sub-menu-button:hover {
    background-color: #ed1a3b;
    color: white;
  }

  .sub-menu-button.active {
    background-color: #ed1a3b;
    color: #020202;
    font-weight: 600;
  }

  /* Desktop/Mobile Toggle */
  .isDesktop {
    display: block;
    @media (max-width: 768px) {
      display: none;
    }
  }

  .isMobile {
    display: none;
    @media (max-width: 768px) {
      display: block;
    }
  }

  .column {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
  }

  .column.tall-short {
    .gallery-item.first {
      flex: 4;
    }
    .gallery-item.second {
      flex: 1.25;
    }
  }

  .column.short-tall {
    .gallery-item.first {
      flex: 1.25;
    }
    .gallery-item.second {
      flex: 4;
    }
  }

  .gallery-item {
    position: relative;
    border-radius: 16px;
    overflow: hidden;
  }

  .column-mobile {
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
    width: 100% !important;
  }

  .column-mobile.col-1 {
    .gallery-item.item-1 {
      flex: 4;
    }
    .gallery-item.item-2 {
      flex: 4;
    }
    .gallery-item.item-3 {
      flex: 2;
    }
  }

  .column-mobile.col-2 {
    .gallery-item.item-1 {
      flex: 2;
    }
    .gallery-item.item-2 {
      flex: 5;
    }
    .gallery-item.item-3 {
      flex: 3;
    }
  }

  .column-mobile.col-3 {
    .gallery-item.item-1 {
      flex: 4;
    }
    .gallery-item.item-2 {
      flex: 3;
    }
    .gallery-item.item-3 {
      flex: 3;
    }
  }

  @media (max-width: 768px) {
    padding: 0px 0;
    .gallery-item {
      border-radius: 12px;
    }
  }
`;

export default SectionHighlight;
